import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Header from "../components/app/Header";
import Footer from "../components/app/Footer";
import ReferralRewardCard from "../components/app/ReferralRewardCard";
import { api, formatErr } from "../lib/api";
import { toast } from "sonner";
import {
  Download,
  CheckCircle,
  MapPin,
  Loader2,
  ArrowRight,
  FileText,
  Camera,
  Lock,
  Beer,
  Trees,
  Mountain,
  Tent,
} from "lucide-react";

/**
 * SecretPitchMap — iter212 Lead Magnet Landing Page
 *
 * Public, SEO-indexed at `/secret-pitch-map`. Captures an email in exchange
 * for the "UK Secret Pitch Map" framework PDF (8 pages: legality cheat
 * sheet, 5 category counts, 3 escape plans, 7 unwritten rules).
 *
 * On submit:
 *   1. Row in `db.lead_magnet_signups` (email + consent + UA + magnet slug).
 *   2. PDF emailed as attachment via Resend (day-1 delivery).
 *   3. Drip scheduler auto-fires day-3 (value-add) + day-7 (community pitch).
 *
 * Zero-work SEO: real HTML content (legality sheet + categories + routes)
 * so Google indexes it as a proper article. HowTo + ItemList JSON-LD.
 */

const CATEGORIES = [
  { icon: Tent, name: "Certificated Locations", count: "≈2,100", why: "The CAMC's 5-pitch member sites — usually £8-14/night on working farms." },
  { icon: Beer, name: "Pub & Brewery Stopovers", count: "≈380", why: "Free pitches in exchange for a meal at the pub. Britstops-style, community-curated." },
  { icon: Mountain, name: "Scottish Wild-Camping Zones", count: "≈450", why: "Legal under the Outdoor Access Code. Only the ones with drivable access." },
  { icon: Lock, name: "MBA Bothies (vehicle-adjacent)", count: "≈80", why: "Free open shelters within 20 minutes' walk of a park-up." },
  { icon: Trees, name: "Forestry & Farm Overnight Pitches", count: "≈220", why: "Forestry England / Natural Resources Wales / independent farmers. £0-8/night." },
];

const LEGALITY = [
  { where: "Scotland", verdict: "✓ Legal", note: "Scottish Outdoor Access Code. Respect Loch Lomond permit zones Mar-Sep." },
  { where: "Dartmoor (England)", verdict: "✓ Legal", note: "Backpack only, permitted zones on the DNP map. 2023 Supreme Court ruling." },
  { where: "Rest of England & Wales", verdict: "Permission required", note: "Farmer / pub / CL / forestry pitches only — this map is where they live." },
  { where: "Northern Ireland", verdict: "Restricted", note: "Mourne Heritage Trust + several forest parks run booked wild-style pitches." },
];

const RULES = [
  "Arrive after 7pm at pub stopovers — you'll get the corner spot, not the delivery-lorry gap.",
  "Check the last 'slept here' date in the app. Over 60 days = phone the number listed first.",
  "Never park on soft verges after October.",
  "Buy something. A pint, a jar of jam, a newspaper — kills bad feeling forever.",
  "One night per spot unless invited to stay longer.",
  "Move on politely if asked, even at legal-in-Scotland pitches.",
  "Flag the pitch in the app after you leave. Keeps the map honest for the next van.",
];

const DOWNLOAD_URL = "/api/lead-magnet/secret-pitch-map.pdf";

export default function SecretPitchMap() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  // iter216 — capture the referrer's ?ref=xxx code and forward it on
  // signup so the referrer's counter ticks up and unlocks fire.
  const [searchParams] = useSearchParams();
  const refCode = (searchParams.get("ref") || "").trim().toLowerCase();
  const [referral, setReferral] = useState(null);

  const jsonLd = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@type": "HowTo",
      name: "The UK Secret Pitch Map — Free & Wild Camping Legally",
      description:
        "The framework for finding free and wild-camping pitches across the UK — legality cheat sheet, five category filters, three ready-made escape plans and the seven unwritten rules.",
      totalTime: "PT8M",
      step: [
        { "@type": "HowToStep", position: 1, name: "Understand the legality", text: "Wild camping is legal in Scotland under the Outdoor Access Code and in permitted Dartmoor zones. Elsewhere in the UK requires landowner permission — which farms, pubs and CLs provide across thousands of pitches." },
        { "@type": "HowToStep", position: 2, name: "Choose your category", text: "Filter the CampCompass map by Certificated Location, Pub Stopover, Scottish Wild Zone, MBA Bothy or Forestry Pitch." },
        { "@type": "HowToStep", position: 3, name: "Follow the seven rules", text: "Arrive late, leave early, out of sight, carry out everything, no fires on peat, buy something, flag the pitch after you leave." },
      ],
    }),
    []
  );

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim() || busy) return;
    setBusy(true);
    try {
      const { data } = await api.post("/lead-magnet/secret-pitch-map/request", {
        email: email.trim(),
        consent: true,
        ref: refCode || undefined,
      });
      setSent(true);
      setReferral(data?.referral || null);
      toast.success("Map sent — check your inbox");
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0e0e0e] text-white flex flex-col" data-testid="secret-pitch-map-page">
      <Header />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />

      {/* HERO */}
      <section className="relative overflow-hidden border-b border-white/10">
        {/* Diagonal orange corner */}
        <div
          className="absolute top-0 left-0 pointer-events-none"
          style={{
            width: "55%",
            height: "55%",
            background: "linear-gradient(135deg, #ff4500 0%, #e03d00 100%)",
            clipPath: "polygon(0 0, 100% 0, 0 100%)",
          }}
        />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 py-14 lg:py-24 grid lg:grid-cols-[1.4fr_1fr] gap-10 items-center">
          <div>
            <span className="mono text-[10px] uppercase tracking-widest font-black text-[#ffb98a] mb-3 inline-flex items-center gap-1.5" data-testid="spm-eyebrow">
              <Lock className="w-3 h-3" /> Members-only guide · 2026-27 · Free PDF
            </span>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-7xl leading-[0.95] mt-3 mb-4">
              Sleep for free in the wildest corners of Britain
              <span className="italic font-light opacity-80"> — legally.</span>
            </h1>
            <p className="text-white/80 text-base lg:text-lg max-w-xl leading-relaxed">
              The <b>UK Secret Pitch Map</b> is the underground handbook UK campers pass around
              in van-life WhatsApp groups. Bothies you can walk to. Farmer's fields for £0. Pub
              car parks that serve breakfast with your MOT. <b>All plotted, all vetted, all legal.</b>
            </p>
            <ul className="mt-6 space-y-2.5 text-sm text-white/80">
              <li className="flex items-start gap-2">
                <span className="text-[#ff4500] flex-shrink-0">🗺️</span>
                <span><b>200+ free & wild-camping pitches</b> across England, Scotland, Wales & NI — colour-coded by legality, midge score, and mobile signal.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#ff4500] flex-shrink-0">🔒</span>
                <span><b>Insider spots that campsite databases refuse to publish</b> — the sub-£12 CLs, the pubs that gift a free pitch when you buy dinner, and the bothies with running water.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#ff4500] flex-shrink-0">⚡</span>
                <span><b>Every entry field-tested by CampCompass members</b> who've slept there this year — no scraped, out-of-date rubbish.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#ff4500] flex-shrink-0">⏳</span>
                <span><b>Grab it before it goes private</b> — some owners have asked us to stop sharing at 5,000 downloads.</span>
              </li>
            </ul>
          </div>

          {/* CAPTURE CARD */}
          <div className="lg:justify-self-end w-full lg:max-w-sm">
            <div className="bg-white text-[#0e0e0e] p-1 shadow-[0_10px_0_-2px_rgba(255,69,0,0.35)]">
              <div className="border-2 border-[#0e0e0e] p-6">
                <div className="mono text-[10px] uppercase tracking-widest font-black text-[#ff4500] mb-2 flex items-center gap-1.5">
                  <FileText className="w-3.5 h-3.5" /> Get the map · 30 seconds
                </div>
                <h2 className="font-display text-2xl leading-tight mb-4">
                  Drop your email. Map sent instantly.
                </h2>

                {sent ? (
                  <>
                    <div className="p-4 border-2 border-[#0a7f3f] bg-[#f4faf6] mb-4" data-testid="spm-sent">
                      <div className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-[#0a7f3f] flex-shrink-0 mt-0.5" />
                        <div className="text-sm text-[#0e0e0e] leading-snug">
                          <div className="font-black uppercase tracking-wider text-[11px] text-[#0a7f3f] mb-1">
                            Sent
                          </div>
                          <p className="mb-2">
                            Check <b>{email}</b>. Map attached; live pins in the app.
                          </p>
                          <a
                            href={DOWNLOAD_URL}
                            target="_blank"
                            rel="noopener"
                            className="inline-flex items-center gap-1.5 text-[#ff4500] font-bold text-xs uppercase tracking-widest underline"
                            data-testid="spm-download-now"
                          >
                            <Download className="w-3.5 h-3.5" /> Download it now
                          </a>
                        </div>
                      </div>
                    </div>
                    {/* iter216 — post-signup referral reward card */}
                    {referral && <ReferralRewardCard referral={referral} compact />}
                  </>
                ) : (
                  <form onSubmit={submit} className="space-y-3" data-testid="spm-form">
                    <input
                      type="email"
                      className="cc-input"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@email.com"
                      required
                      data-testid="spm-email"
                    />
                    <button
                      type="submit"
                      disabled={busy}
                      className="w-full inline-flex items-center justify-center gap-2 bg-[#ff4500] text-white font-black text-xs uppercase tracking-widest px-4 py-3 shadow-[0_3px_0_0_#0e0e0e] hover:translate-y-[1px] hover:shadow-[0_2px_0_0_#0e0e0e] transition-transform disabled:opacity-60"
                      data-testid="spm-submit"
                    >
                      {busy ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Download className="w-3.5 h-3.5" />
                      )}
                      Send me the pitch map
                    </button>
                    <p className="text-[10px] text-[#888] leading-tight">
                      Instant PDF + live map link. Occasional trip tips after. Unsubscribe with one click.
                    </p>
                  </form>
                )}

                <div className="mt-4 pt-4 border-t border-[#eee]">
                  <a
                    href={DOWNLOAD_URL}
                    target="_blank"
                    rel="noopener"
                    className="text-[11px] mono uppercase tracking-widest underline text-[#555]"
                    data-testid="spm-skip-download"
                  >
                    Or download without email <ArrowRight className="inline w-3 h-3 -mt-0.5" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* LEGALITY */}
      <section className="border-b border-white/10 bg-[#141414] max-w-full">
        <div className="max-w-[1400px] mx-auto w-full px-6 lg:px-10 py-12 lg:py-16">
          <div className="mb-8">
            <div className="mono text-[10px] uppercase tracking-widest font-black text-[#ffb98a] mb-1.5">Part 1</div>
            <h2 className="font-display text-2xl md:text-3xl">The one page every UK camper should own</h2>
            <p className="text-white/60 text-sm mt-2 max-w-2xl">
              Wild-camping legality in the UK is a maze. In plain English:
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            {LEGALITY.map((l, i) => (
              <div key={i} className="p-5 border border-white/10 bg-black/30" data-testid={`spm-legal-${i + 1}`}>
                <div className="flex items-baseline justify-between mb-2 gap-3">
                  <div className="font-bold text-[15px]">{l.where}</div>
                  <div className={`mono text-[10px] uppercase tracking-widest font-black ${l.verdict.startsWith("✓") ? "text-[#4ade80]" : "text-[#ff8a5a]"}`}>
                    {l.verdict}
                  </div>
                </div>
                <p className="text-sm text-white/70 leading-relaxed">{l.note}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="border-b border-white/10">
        <div className="max-w-[1400px] mx-auto w-full px-6 lg:px-10 py-12 lg:py-16">
          <div className="mb-8">
            <div className="mono text-[10px] uppercase tracking-widest font-black text-[#ffb98a] mb-1.5">Part 2</div>
            <h2 className="font-display text-2xl md:text-3xl">Where the free pitches actually live</h2>
            <p className="text-white/60 text-sm mt-2 max-w-2xl">
              Each category is a filter in the app. Toggle one, see every pin in that layer live.
            </p>
          </div>
          <div className="space-y-4">
            {CATEGORIES.map((c, i) => {
              const Icon = c.icon;
              return (
                <div key={i} className="grid grid-cols-[auto_1fr_auto] items-center gap-4 py-4 border-b border-white/10" data-testid={`spm-cat-${i + 1}`}>
                  <div className="w-10 h-10 bg-[#ff4500] flex items-center justify-center flex-shrink-0">
                    <Icon className="w-5 h-5 text-white" strokeWidth={2.25} />
                  </div>
                  <div>
                    <div className="font-bold text-[15px] mb-0.5">{c.name}</div>
                    <p className="text-sm text-white/70 leading-relaxed">{c.why}</p>
                  </div>
                  <div className="mono text-xs uppercase tracking-widest font-black text-[#ff4500] whitespace-nowrap">
                    {c.count}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* 7 RULES */}
      <section className="border-b border-white/10 bg-[#141414]">
        <div className="max-w-[1400px] mx-auto w-full px-6 lg:px-10 py-12 lg:py-16">
          <div className="mb-8">
            <div className="mono text-[10px] uppercase tracking-widest font-black text-[#ffb98a] mb-1.5">Part 3</div>
            <h2 className="font-display text-2xl md:text-3xl">The 7 unwritten rules</h2>
            <p className="text-white/60 text-sm mt-2 max-w-2xl">
              Every rude camper burns a pitch for the next twenty. These keep the network open.
            </p>
          </div>
          <ol className="grid md:grid-cols-2 gap-x-8 gap-y-4">
            {RULES.map((r, i) => (
              <li key={i} className="flex gap-3" data-testid={`spm-rule-${i + 1}`}>
                <span className="flex-shrink-0 mono text-lg font-black text-[#ff4500]">{String(i + 1).padStart(2, "0")}</span>
                <p className="text-sm text-white/85 leading-relaxed pt-1">{r}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* CTAs */}
      <section className="max-w-[1400px] mx-auto w-full px-6 lg:px-10 py-14 lg:py-20">
        <div className="grid md:grid-cols-3 gap-4">
          <Link
            to="/browse?free=1"
            className="group p-6 border-2 border-[#ff4500] bg-[#ff4500] text-white hover:bg-[#e03d00] transition-colors"
            data-testid="spm-cta-map"
          >
            <MapPin className="w-6 h-6 mb-3" />
            <div className="mono text-[10px] uppercase tracking-widest font-black opacity-80 mb-1">Open the live map</div>
            <div className="font-display text-xl leading-tight mb-2">
              Every pin, live-flagged, member-verified
            </div>
            <div className="mono text-[11px] uppercase tracking-widest opacity-80">
              Open now <ArrowRight className="inline w-3 h-3 -mt-0.5" />
            </div>
          </Link>

          <Link
            to="/share-your-trip"
            className="group p-6 border-2 border-white/30 bg-transparent text-white hover:bg-white hover:text-[#0e0e0e] transition-colors"
            data-testid="spm-cta-share"
          >
            <Camera className="w-6 h-6 mb-3" />
            <div className="mono text-[10px] uppercase tracking-widest font-black opacity-80 mb-1">Share a pitch</div>
            <div className="font-display text-xl leading-tight mb-2">
              Add the spot you slept at last night
            </div>
            <div className="mono text-[11px] uppercase tracking-widest opacity-80">
              1-click signup <ArrowRight className="inline w-3 h-3 -mt-0.5" />
            </div>
          </Link>

          <Link
            to="/community"
            className="group p-6 border-2 border-white/30 bg-transparent text-white hover:bg-white hover:text-[#0e0e0e] transition-colors"
            data-testid="spm-cta-community"
          >
            <FileText className="w-6 h-6 mb-3" />
            <div className="mono text-[10px] uppercase tracking-widest font-black opacity-80 mb-1">Join the campfire</div>
            <div className="font-display text-xl leading-tight mb-2">
              Where members trade the newest pins
            </div>
            <div className="mono text-[11px] uppercase tracking-widest opacity-80">
              Read the threads <ArrowRight className="inline w-3 h-3 -mt-0.5" />
            </div>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
